import React, { useState, useEffect } from "react"
import { Sidebar, FlashcardPopup } from "./components"
import { HomePage, ProfilePage, SettingsPage, FlashcardsPage } from "./pages"
import LearnPage from "./pages/LearnPage"
import { useFirebase } from "./context/FirebaseContext"
import { doc, updateDoc, getDoc } from "firebase/firestore"
import { db } from "./firebase/firebase"
import { SpeedInsights } from "@vercel/speed-insights/react"

const App = () => {
	// State variables
	const [streak, setStreak] = useState(0)
	const [hasCheckedInToday, setHasCheckedInToday] = useState(false)
	const [activeTab, setActiveTab] = useState("home")
	const [searchQuery, setSearchQuery] = useState("")
	const [selectedCourse, setSelectedCourse] = useState(null)
	const [showLanguageMenu, setShowLanguageMenu] = useState(false)
	const [selectedFlashcard, setSelectedFlashcard] = useState(null)
	const [notifications, setNotifications] = useState(false)
	const [soundEffects, setSoundEffects] = useState(false)

	// Known words tracking
	const [knownWords, setKnownWords] = useState(new Set())
	const [knownWordsLoading, setKnownWordsLoading] = useState(false)

	// Get data from Firebase context
	const {
		currentLanguage,
		currentCourses,
		currentFlashcards,
		isLoading,
		changeLanguage,
		getFilteredCourses,
		currentUser,
		getUserData,
		updateUserStreak,
	} = useFirebase()

	const filteredCourses = getFilteredCourses(searchQuery)

	// Load known words when user changes or language changes
	useEffect(() => {
		const loadKnownWords = async () => {
			setKnownWordsLoading(true)
			try {
				if (currentUser && currentLanguage) {
					// Load from Firebase for logged-in users
					const userData = await getUserData(currentUser.uid)
					const languageCode = currentLanguage.code
					const userKnownWords = userData?.knownWords?.[languageCode] || []
					setKnownWords(new Set(userKnownWords))
				} else if (currentLanguage) {
					// Load from localStorage for guest users
					const languageCode = currentLanguage.code
					const savedKnownWords = localStorage.getItem(
						`hopKnownWords_${languageCode}`
					)
					if (savedKnownWords) {
						setKnownWords(new Set(JSON.parse(savedKnownWords)))
					} else {
						setKnownWords(new Set())
					}
				}
			} catch (error) {
				console.error("Error loading known words:", error)
				setKnownWords(new Set())
			} finally {
				setKnownWordsLoading(false)
			}
		}

		loadKnownWords()
	}, [currentUser, currentLanguage, getUserData])

	// Load streak when user changes (login/logout)
	useEffect(() => {
		const loadStreak = async () => {
			const today = new Date().toISOString().split("T")[0]

			if (currentUser) {
				// Load streak from Firebase for logged-in users
				try {
					const userData = await getUserData(currentUser.uid)
					if (userData && userData.streak !== undefined) {
						// Check if streak should be reset (if last update was not today or yesterday)
						const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
							.toISOString()
							.split("T")[0]

						if (userData.lastStreakUpdate) {
							if (userData.lastStreakUpdate === today) {
								setStreak(userData.streak)
								setHasCheckedInToday(true)
							} else if (userData.lastStreakUpdate === yesterday) {
								setStreak(userData.streak)
								setHasCheckedInToday(false)
							} else {
								// Reset streak if more than 1 day has passed
								setStreak(0)
								setHasCheckedInToday(false)
								await updateUserStreak(currentUser.uid, 0)
							}
						} else {
							setStreak(userData.streak)
							setHasCheckedInToday(false)
						}
					} else {
						setStreak(0)
						setHasCheckedInToday(false)
					}
				} catch (error) {
					console.error("Error loading user streak:", error)
					setStreak(0)
					setHasCheckedInToday(false)
				}
			} else {
				// Load streak from localStorage for non-logged-in users
				const savedStreak = localStorage.getItem("hopLanguageAppStreak")
				const savedDate = localStorage.getItem("hopLanguageAppStreakDate")

				if (savedStreak && savedDate) {
					const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
						.toISOString()
						.split("T")[0]
					if (savedDate === today) {
						setStreak(parseInt(savedStreak))
						setHasCheckedInToday(true)
					} else if (savedDate === yesterday) {
						setStreak(parseInt(savedStreak))
						setHasCheckedInToday(false)
					} else {
						// Reset streak if more than 1 day has passed
						setStreak(0)
						setHasCheckedInToday(false)
						localStorage.removeItem("hopLanguageAppStreak")
						localStorage.removeItem("hopLanguageAppStreakDate")
					}
				} else {
					setStreak(0)
					setHasCheckedInToday(false)
				}
			}
		}

		loadStreak()
	}, [currentUser, getUserData, updateUserStreak])

	useEffect(() => {
		const savedSettings = localStorage.getItem("hopLanguageAppSettings")
		if (savedSettings) {
			const parsed = JSON.parse(savedSettings)
			setNotifications(parsed.notifications ?? false)
			setSoundEffects(parsed.soundEffects ?? false)
		}
	}, [])

	// Mark flashcard as known
	const handleMarkAsKnown = async flashcardId => {
		if (!currentLanguage) return

		const languageCode = currentLanguage.code
		const newKnownWords = new Set(knownWords)
		newKnownWords.add(flashcardId)
		setKnownWords(newKnownWords)

		try {
			if (currentUser) {
				// Save to Firebase for logged-in users
				const userRef = doc(db, "users", currentUser.uid)
				const userData = await getUserData(currentUser.uid)

				const updatedKnownWords = {
					...userData?.knownWords,
					[languageCode]: Array.from(newKnownWords),
				}

				await updateDoc(userRef, {
					knownWords: updatedKnownWords,
					lastKnownWordUpdate: new Date().toISOString(),
				})

				// Play success sound if enabled
				if (soundEffects) {
					const audio = new Audio("/sounds/success.mp3")
					audio.volume = 0.3
					audio.play().catch(e => console.log("Audio play failed:", e))
				}

				// Send notification if enabled
				if (
					notifications &&
					"Notification" in window &&
					Notification.permission === "granted"
				) {
					new Notification("Word Learned!", {
						body: "Great job! You've marked another word as known.",
						icon: "/logo192.png",
					})
				}
			} else {
				// Save to localStorage for guest users
				localStorage.setItem(
					`hopKnownWords_${languageCode}`,
					JSON.stringify(Array.from(newKnownWords))
				)

				// Play success sound if enabled
				if (soundEffects) {
					const audio = new Audio("/sounds/success.mp3")
					audio.volume = 0.3
					audio.play().catch(e => console.log("Audio play failed:", e))
				}
			}
		} catch (error) {
			console.error("Error saving known word:", error)
			// Revert the change if saving failed
			setKnownWords(knownWords)
		}
	}

	// Get known words count for current language
	const getKnownWordsCount = () => {
		return knownWords.size
	}

	// Get learning progress percentage
	const getLearningProgress = () => {
		if (currentFlashcards.length === 0) return 0
		return Math.round((knownWords.size / currentFlashcards.length) * 100)
	}

	// Filter flashcards to show unknown words first
	const getFilteredFlashcards = (showOnlyUnknown = false) => {
		if (showOnlyUnknown) {
			return currentFlashcards.filter(card => !knownWords.has(card.id))
		}
		// Sort to show unknown words first, then known words
		return currentFlashcards.sort((a, b) => {
			const aKnown = knownWords.has(a.id)
			const bKnown = knownWords.has(b.id)
			if (aKnown === bKnown) return 0
			return aKnown ? 1 : -1
		})
	}

	// Handle tab switching
	const handleTabClick = tab => {
		setActiveTab(tab)
		setSelectedCourse(null)
		setSelectedFlashcard(null)

		if (soundEffects) {
			const audio = new Audio("/sounds/click.mp3")
			audio.volume = 0.5
			audio.play().catch(e => console.log("Audio play failed:", e))
		}
	}

	const handleLanguageChange = langCode => {
		changeLanguage(langCode) // Use changeLanguage from Firebase context
		setSelectedCourse(null) // Reset selected course when changing language
		setSelectedFlashcard(null) // Reset selected flashcard

		// Play sound effect if enabled
		if (soundEffects) {
			const audio = new Audio("/sounds/language-change.mp3")
			audio.volume = 0.5
			audio.play().catch(e => console.log("Audio play failed:", e))
		}

		// Send notification if enabled
		if (
			notifications &&
			"Notification" in window &&
			Notification.permission === "granted"
		) {
			new Notification("Language Changed", {
				body: `You are now learning ${
					currentLanguage?.name || "a new language"
				}`,
			})
		}
	}

	// Handle course selection
	const handleCourseClick = course => {
		setSelectedCourse(course)

		// Play sound effect if enabled
		if (soundEffects) {
			const audio = new Audio("/sounds/select.mp3")
			audio.volume = 0.5
			audio.play().catch(e => console.log("Audio play failed:", e))
		}
	}

	// Handle flashcard selection
	const handleFlashcardSelect = flashcard => {
		setSelectedFlashcard(flashcard)

		// Play sound effect if enabled
		if (soundEffects) {
			const audio = new Audio("/sounds/card-flip.mp3")
			audio.volume = 0.5
			audio.play().catch(e => console.log("Audio play failed:", e))
		}
	}

	// Handle flashcard close
	const handleCloseFlashcard = () => {
		setSelectedFlashcard(null)
	}

	// Handle continue learning button
	const handleContinueLearning = () => {
		if (selectedCourse) {
			// In a real app, this would navigate to the specific lesson
			alert(`Starting lesson in ${selectedCourse.name} course!`)
		} else {
			// Find the course with most progress
			const mostProgressCourse = [...currentCourses].sort(
				(a, b) => b.completed / b.lessons - a.completed / a.lessons
			)[0]
			if (mostProgressCourse) {
				setSelectedCourse(mostProgressCourse)
				alert(`Continuing with ${mostProgressCourse.name} course!`)
			}
		}
	}

	// Increase streak (updated to work with Firebase)
	const handleIncreaseStreak = async () => {
		const today = new Date().toISOString().split("T")[0]

		if (currentUser) {
			// Update streak in Firebase for logged-in users
			try {
				const userData = await getUserData(currentUser.uid)

				// Check if user already checked in today
				if (userData?.lastStreakUpdate === today) {
					return // Already checked in today
				}

				const newStreak = Math.min(streak + 1, 7) // Cap at 7 days
				setStreak(newStreak)
				setHasCheckedInToday(true)

				await updateUserStreak(currentUser.uid, newStreak)

				// Play sound effect if enabled
				if (soundEffects) {
					const audio = new Audio("/sounds/streak.mp3")
					audio.volume = 0.5
					audio.play().catch(e => console.log("Audio play failed:", e))
				}

				// Send notification if enabled
				if (
					notifications &&
					"Notification" in window &&
					Notification.permission === "granted"
				) {
					new Notification("Streak Increased!", {
						body: `You're on a ${newStreak} day streak! Keep it up!`,
					})
				}
			} catch (error) {
				console.error("Error updating streak:", error)
			}
		} else {
			// Update streak in localStorage for non-logged-in users
			const savedDate = localStorage.getItem("hopLanguageAppStreakDate")

			// Check if user already checked in today
			if (savedDate === today) {
				return // Already checked in today
			}

			const newStreak = Math.min(streak + 1, 7) // Cap at 7 days
			setStreak(newStreak)
			setHasCheckedInToday(true)

			// Save to localStorage
			localStorage.setItem("hopLanguageAppStreak", newStreak.toString())
			localStorage.setItem("hopLanguageAppStreakDate", today)

			// Play sound effect if enabled
			if (soundEffects) {
				const audio = new Audio("/sounds/streak.mp3")
				audio.volume = 0.5
				audio.play().catch(e => console.log("Audio play failed:", e))
			}

			// Send notification if enabled
			if (
				notifications &&
				"Notification" in window &&
				Notification.permission === "granted"
			) {
				new Notification("Streak Increased!", {
					body: `You're on a ${newStreak} day streak! Keep it up!`,
				})
			}
		}
	}

	// Handle search input
	const handleSearchChange = e => {
		setSearchQuery(e.target.value)
	}

	// Handle settings changes
	const handleSettingsChange = (settingType, value) => {
		switch (settingType) {
			case "notifications":
				setNotifications(value)
				break
			case "soundEffects":
				setSoundEffects(value)
				break
			default:
				break
		}

		// Save settings to localStorage
		const currentSettings = JSON.parse(
			localStorage.getItem("hopLanguageAppSettings") || "{}"
		)
		const updatedSettings = {
			...currentSettings,
			[settingType]: value,
		}
		localStorage.setItem(
			"hopLanguageAppSettings",
			JSON.stringify(updatedSettings)
		)
	}

	// Render content based on active tab
	const renderContent = () => {
		// Show loading indicator while data is being fetched
		if (isLoading || knownWordsLoading) {
			return (
				<div className='flex justify-center items-center h-64'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3A6E]'></div>
				</div>
			)
		}

		// If there's no current language data, show an error
		if (!currentLanguage) {
			return (
				<div className='text-center py-10'>
					<div className='text-[#FF3A6E] text-xl mb-4'>Error Loading Data</div>
					<p className='text-[#A0A0A0]'>
						Could not load language data. Please try again later.
					</p>
				</div>
			)
		}

		switch (activeTab) {
			case "home":
				return (
					<HomePage
						currentLanguage={currentLanguage}
						streak={streak}
						hasCheckedInToday={hasCheckedInToday}
						handleIncreaseStreak={handleIncreaseStreak}
						handleTabClick={handleTabClick}
						handleLanguageChange={handleLanguageChange}
					/>
				)
			case "learn":
				return (
					<LearnPage
						currentLanguage={currentLanguage}
						currentFlashcards={getFilteredFlashcards()}
						handleFlashcardSelect={handleFlashcardSelect}
						handleTabClick={handleTabClick}
						knownWords={knownWords}
						knownWordsCount={getKnownWordsCount()}
						onMarkAsKnown={handleMarkAsKnown}
					/>
				)
			case "profile":
				return (
					<ProfilePage
						currentLanguage={currentLanguage}
						currentFlashcards={currentFlashcards}
						streak={streak}
						handleTabClick={handleTabClick}
						knownWordsCount={getKnownWordsCount()}
						learningProgress={getLearningProgress()}
					/>
				)
			case "settings":
				return (
					<SettingsPage
						currentLanguage={currentLanguage}
						handleLanguageChange={handleLanguageChange}
						notifications={notifications}
						soundEffects={soundEffects}
						handleSettingsChange={handleSettingsChange}
					/>
				)
			case "flashcards":
				return (
					<FlashcardsPage
						currentLanguage={currentLanguage}
						currentFlashcards={getFilteredFlashcards()}
						handleFlashcardSelect={handleFlashcardSelect}
						handleTabClick={handleTabClick}
						setShowLanguageMenu={setShowLanguageMenu}
						knownWords={knownWords}
						knownWordsCount={getKnownWordsCount()}
						totalFlashcards={currentFlashcards.length}
					/>
				)
			default:
				return <div>Tab content not found</div>
		}
	}

	return (
		<div className='flex flex-col md:flex-row bg-[#121212] min-h-screen text-white font-poppins overflow-hidden relative'>
			{/* Sidebar */}
			<Sidebar activeTab={activeTab} handleTabClick={handleTabClick} />

			{/* Main Content */}
			<div className='flex-1 p-4 md:p-6 overflow-y-auto pt-16 md:pt-6'>
				{renderContent()}
			</div>

			{/* Flashcard Popup */}
			{selectedFlashcard && (
				<FlashcardPopup
					flashcard={selectedFlashcard}
					handleCloseFlashcard={handleCloseFlashcard}
					onMarkAsKnown={handleMarkAsKnown}
					isKnown={knownWords.has(selectedFlashcard.id)}
				/>
			)}

			{/* Vercel Speed Insights */}
			<SpeedInsights />
		</div>
	)
}

export default App
